import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getProductVariants } from '../api/products';
import type { VariantSearchResult } from '../api/products';
import type { BlackoutType } from '../components/ItemAvailability/types';

export type SnoozeScope = 'WHOLE_PRODUCT' | 'SPECIFIC_VARIANTS';

/** An item chosen in the snooze picker, before variant scoping is applied. */
export interface ScopedSelectedItem {
  type: BlackoutType;
  code: string;
  name: string;
}

/** Per-product variant state. One entry exists for every selected product. */
export interface ProductScopeEntry {
  code: string;
  name: string;
  variants: VariantSearchResult[];
  isLoading: boolean;
  scope: SnoozeScope;
  selectedVariants: VariantSearchResult[];
  /** A scope choice is only meaningful when the product has 2+ variants. */
  hasMultipleVariants: boolean;
  /** True once we know the product has at most one variant — drives the hint. */
  showSingleVariantHint: boolean;
  /** On SPECIFIC_VARIANTS but nothing picked yet — blocks submit. */
  isInvalid: boolean;
}

/** Code arrays + audit metadata, ready to hand to `createItemBlackout`. */
export interface SnoozeSubmitPayload {
  productCodes: string[];
  bundleCodes: string[];
  variantCodes: string[];
  modifierCodes: string[];
  optionValueCodes: string[];
  auditItems: Array<{ type: BlackoutType; code: string; name: string }>;
}

interface UseProductVariantScopeArgs {
  /** True when the currently selected snooze type is a product. */
  isProductType: boolean;
  /** The items currently selected in the snooze form. */
  selectedItems: ScopedSelectedItem[];
}

export interface ProductVariantScope {
  /** One entry per selected product, in selection order. */
  entries: ProductScopeEntry[];
  /** Entries that actually need a scope control rendered. */
  scopedEntries: ProductScopeEntry[];
  isLoadingAnyVariants: boolean;
  /** Any product left on SPECIFIC_VARIANTS with zero picks. */
  hasInvalidVariantScope: boolean;
  /** True when at least one product is narrowed to specific variants. */
  hasAnySpecificVariants: boolean;
  setScope: (productCode: string, scope: SnoozeScope) => void;
  toggleVariant: (productCode: string, variant: VariantSearchResult) => void;
  resetVariantState: () => void;
  buildSubmitPayload: (selectedItems: ScopedSelectedItem[]) => SnoozeSubmitPayload;
  /** How many things will actually be snoozed — drives the submit button label. */
  getSnoozeUnitCount: (selectedItems: ScopedSelectedItem[]) => number;
}

/** Mutable per-product state; the fetched variant list is cached separately. */
interface ScopeState {
  scope: SnoozeScope;
  selectedVariants: VariantSearchResult[];
}

const WHOLE_PRODUCT_STATE: ScopeState = { scope: 'WHOLE_PRODUCT', selectedVariants: [] };

/**
 * Drives the "Snoozing scope" drill-down used by every snooze entry point.
 * Every selected product gets its own variant list and its own "Whole product"
 * vs "Specific variants only" choice, so a multi-item selection can mix the two
 * (e.g. snooze all of product A but only the Large variant of product B).
 *
 * Variant lists are cached by product code for the lifetime of the hook, so
 * de-selecting and re-selecting a product doesn't refetch.
 */
export function useProductVariantScope({
  isProductType,
  selectedItems,
}: UseProductVariantScopeArgs): ProductVariantScope {
  // Fetched variants, keyed by product code. Survives de-selection so the same
  // product can be picked again without another round trip.
  // A code is "loading" precisely while it has no entry here — every settled
  // fetch writes a list (an empty one on failure), so no separate flag is needed.
  const [variantsByCode, setVariantsByCode] = useState<Record<string, VariantSearchResult[]>>({});
  const [scopeByCode, setScopeByCode] = useState<Record<string, ScopeState>>({});

  // Codes already requested (in flight or resolved) so the effect never
  // re-fetches the same product after a re-render.
  const requestedCodes = useRef<Set<string>>(new Set());

  // Results are keyed by product code and merged into a record, so a response
  // that lands after the selection changed is harmless — we only need to stop
  // writing state once the component is gone. Cancelling per effect-run would
  // strand in-flight codes as permanently loading, since `requestedCodes` keeps
  // them from being fetched again.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const selectedProducts = useMemo(
    () => (isProductType ? selectedItems.filter((item) => item.type === 'PRODUCT') : []),
    [isProductType, selectedItems]
  );

  // Stable dependency: the effect should only re-run when the set of selected
  // product codes changes, not when the parent re-creates the items array.
  const selectedProductCodesKey = selectedProducts.map((p) => p.code).join('|');

  const resetVariantState = useCallback(() => {
    setScopeByCode({});
  }, []);

  const cacheVariants = useCallback((code: string, variants: VariantSearchResult[]) => {
    if (!isMounted.current) return;
    setVariantsByCode((prev) => ({ ...prev, [code]: variants }));
  }, []);

  const loadVariants = useCallback(
    (code: string) => {
      requestedCodes.current.add(code);
      getProductVariants(code)
        .then((variants) => cacheVariants(code, variants))
        // getProductVariants already swallows 404s; treat any other failure as
        // "no variants" so the product falls back to a whole-product snooze.
        .catch(() => cacheVariants(code, []));
    },
    [cacheVariants]
  );

  // Fetch variants for any newly selected product. Nothing is dropped when a
  // product is de-selected — `entries` is derived from the current selection,
  // and keeping the cache makes re-selection instant.
  useEffect(() => {
    const codes = selectedProductCodesKey ? selectedProductCodesKey.split('|') : [];
    codes.filter((code) => code && !requestedCodes.current.has(code)).forEach(loadVariants);
  }, [selectedProductCodesKey, loadVariants]);

  const entries: ProductScopeEntry[] = useMemo(
    () =>
      selectedProducts.map((product) => {
        const resolved = variantsByCode[product.code];
        const variants = resolved ?? [];
        // Not resolved yet == still loading. Deriving it from the cache rather
        // than a separate flag means the very first render after a selection
        // already reads as loading, so the single-variant hint can't flicker.
        const isLoading = resolved === undefined;
        const state = scopeByCode[product.code] ?? WHOLE_PRODUCT_STATE;
        const hasMultipleVariants = variants.length >= 2;
        const isSpecific = hasMultipleVariants && state.scope === 'SPECIFIC_VARIANTS';

        return {
          code: product.code,
          name: product.name,
          variants,
          isLoading,
          scope: state.scope,
          selectedVariants: state.selectedVariants,
          hasMultipleVariants,
          showSingleVariantHint: !isLoading && variants.length <= 1,
          isInvalid: isSpecific && state.selectedVariants.length === 0,
        };
      }),
    [selectedProducts, variantsByCode, scopeByCode]
  );

  const scopedEntries = useMemo(() => entries.filter((e) => e.hasMultipleVariants), [entries]);

  const isLoadingAnyVariants = entries.some((entry) => entry.isLoading);
  const hasInvalidVariantScope = entries.some((entry) => entry.isInvalid);
  const hasAnySpecificVariants = entries.some(
    (entry) => entry.hasMultipleVariants && entry.scope === 'SPECIFIC_VARIANTS'
  );

  // Switching back to "Whole product" drops that product's picked variants so a
  // stale selection can't leak into the submitted payload.
  const setScope = useCallback((productCode: string, next: SnoozeScope) => {
    setScopeByCode((prev) => ({
      ...prev,
      [productCode]: {
        scope: next,
        selectedVariants: next === 'WHOLE_PRODUCT' ? [] : (prev[productCode]?.selectedVariants ?? []),
      },
    }));
  }, []);

  const toggleVariant = useCallback((productCode: string, variant: VariantSearchResult) => {
    setScopeByCode((prev) => {
      const current = prev[productCode] ?? WHOLE_PRODUCT_STATE;
      const isSelected = current.selectedVariants.some((v) => v.code === variant.code);
      return {
        ...prev,
        [productCode]: {
          scope: current.scope,
          selectedVariants: isSelected
            ? current.selectedVariants.filter((v) => v.code !== variant.code)
            : [...current.selectedVariants, variant],
        },
      };
    });
  }, []);

  /**
   * Split the selection into the code arrays the blackout API expects.
   * A product narrowed to specific variants contributes ONLY its variant codes —
   * the parent product is intentionally left available so sibling variants stay
   * orderable. Everything else passes through by type.
   */
  const buildSubmitPayload = useCallback(
    (items: ScopedSelectedItem[]): SnoozeSubmitPayload => {
      const byCode = new Map(entries.map((entry) => [entry.code, entry]));
      const payload: SnoozeSubmitPayload = {
        productCodes: [],
        bundleCodes: [],
        variantCodes: [],
        modifierCodes: [],
        optionValueCodes: [],
        auditItems: [],
      };

      for (const item of items) {
        if (item.type === 'PRODUCT') {
          const entry = byCode.get(item.code);
          if (entry?.hasMultipleVariants && entry.scope === 'SPECIFIC_VARIANTS') {
            for (const variant of entry.selectedVariants) {
              payload.variantCodes.push(variant.code);
              payload.auditItems.push({ type: 'VARIANT', code: variant.code, name: variant.name });
            }
            continue;
          }
          payload.productCodes.push(item.code);
        } else if (item.type === 'BUNDLE') {
          payload.bundleCodes.push(item.code);
        } else if (item.type === 'MODIFIER') {
          payload.modifierCodes.push(item.code);
        } else if (item.type === 'OPTION_VALUE') {
          payload.optionValueCodes.push(item.code);
        } else {
          payload.variantCodes.push(item.code);
        }
        payload.auditItems.push({ type: item.type, code: item.code, name: item.name });
      }

      return payload;
    },
    [entries]
  );

  const getSnoozeUnitCount = useCallback(
    (items: ScopedSelectedItem[]) => {
      const payload = buildSubmitPayload(items);
      return (
        payload.productCodes.length +
        payload.bundleCodes.length +
        payload.variantCodes.length +
        payload.modifierCodes.length +
        payload.optionValueCodes.length
      );
    },
    [buildSubmitPayload]
  );

  return {
    entries,
    scopedEntries,
    isLoadingAnyVariants,
    hasInvalidVariantScope,
    hasAnySpecificVariants,
    setScope,
    toggleVariant,
    resetVariantState,
    buildSubmitPayload,
    getSnoozeUnitCount,
  };
}
